import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface StatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: string;
  autoCloseDelay?: number; // in milliseconds
}

export const StatusModal: React.FC<StatusModalProps> = ({
  isOpen,
  onClose,
  status,
  autoCloseDelay = 4000 // Default to 4 seconds
}) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (isOpen && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose, autoCloseDelay]);

  if (!isOpen) return null;

  // Map technical statuses to user-friendly, translated labels and colors
  const statusMap: Record<string, { label: string; color: string }> = {
    publish_complete: {
      label: t('status.published', i18n.language === 'fr' ? 'Post publié' : 'Post Published'),
      color: 'green',
    },
    published: {
      label: t('status.published', i18n.language === 'fr' ? 'Post publié' : 'Post Published'),
      color: 'green',
    },
    success: {
      label: t('status.published', i18n.language === 'fr' ? 'Post publié' : 'Post Published'),
      color: 'green',
    },
  };
  const normalizedStatus = status.toLowerCase();
  const mapped = statusMap[normalizedStatus] || null;
  const isSuccess = Boolean(mapped && mapped.color === 'green');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl border border-black shadow-lg p-6 max-w-md w-full mx-4 relative">
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex flex-col items-center text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
            isSuccess ? 'bg-green-100' : 'bg-blue-100'
          }`}>
            {isSuccess ? (
              <CheckCircle className="w-10 h-10 text-green-600" />
            ) : (
              <AlertCircle className="w-10 h-10 text-blue-600" />
            )}
          </div>
          
          <h3 className="text-xl font-semibold mb-2">
            {t('common.postStatusModalTitle', 'Post Status')}
          </h3>
          
          <div className={`px-4 py-2 rounded-full text-sm font-medium mb-4 ${
            isSuccess
              ? 'bg-green-100 text-green-700'
              : 'bg-blue-100 text-blue-700'
          }`}>
            {mapped ? mapped.label : t(`statusDropdown.${normalizedStatus}`, t(`status.${normalizedStatus}`, status))}
          </div>
          
          <p className="text-gray-600 text-sm">
            {isSuccess 
              ? t('common.postStatusSuccess', 'Your post has been successfully published!')
              : t('common.postStatusProcessing', 'Your post is currently being processed.')}
          </p>
        </div>
      </div>
    </div>
  );
}; 